import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Check, Loader2, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { AuthLayout } from './AuthLayout';
import { send } from '../components';
import { toast } from 'sonner';
import { encrtypth } from '../service/CoreFunctions';
import { PasswordInput } from '../components/ui/PasswordInput';

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { token } = useParams()
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const passwordRequirements = [
    { met: password.length >= 8, text: t("passwordMinLength") },
    { met: /[A-Z]/.test(password), text: t("passwordUppercase") },
    { met: /[0-9]/.test(password), text: t("passwordNumber") },
  ];

  const isPasswordValid = passwordRequirements.every(r => r.met);
  const isConfirmValid = confirmPassword.length > 0 && password === confirmPassword;

  const canSubmit = isPasswordValid && !loading;


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isPasswordValid) {
      toast.error(t("passwordRequirementsNotMet"));
      return;
    }
    if (!isConfirmValid) {
      toast.error(t("passwordsNotMatch"));
      return;
    }

    const res = await send('io0015', {
      password: encrtypth(password),
      password_confirmation: encrtypth(confirmPassword),
      token
    }, setLoading);
    if (res.status === "error") {
      toast.error(res.message);
    } else {
      toast.success(res);
    }
  };

  return (
    <AuthLayout title={t('resetPassword')} subtitle={t('welcomeBack')}>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-medium">
            {t("password")}
          </Label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <PasswordInput
              id="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-11"
              required
            />
          </div>

          {password && (
            <div className="flex flex-wrap gap-2 mt-2">
              {passwordRequirements.map((req, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-full transition-colors ${req.met
                    ? "bg-success/10 text-success"
                    : "bg-muted text-muted-foreground"
                    }`}
                >
                  <Check
                    className={`h-3 w-3 ${req.met ? "opacity-100" : "opacity-40"
                      }`}
                  />
                  {req.text}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirmPassword" className="text-sm font-medium">
            {t("confirmPassword")}
          </Label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <PasswordInput
              id="confirmPassword"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="pl-11"
              required
            />
          </div>
        </div>

        <Button
          type="submit"
          variant="gradient"
          size="lg"
          className="w-full"
          disabled={!canSubmit}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('loading')}
            </>
          ) : (
            t('resetPassword')
          )}
        </Button>
        <div className="flex items-center justify-end">
          <Link
            to="/login"
            className="text-sm text-primary hover:text-primary/80 font-medium"
          >
            {t('login')}
          </Link>
        </div>
        <p className="text-center text-sm text-muted-foreground">
          {t('noAccount')}{' '}
          <Link to="/register" className="text-primary hover:text-primary/80 font-semibold">
            {t('signUp')}
          </Link>
        </p>
      </form>

      <p className="text-center text-xs text-muted-foreground mt-8">
        {t('termsAgree')}
      </p>
    </AuthLayout>
  );
};

export default ResetPassword;
